import { __assign } from "tslib";
import { useRef, useCallback, useEffect, useContext } from 'react';
import ddt from '@wix/da-ddt';
import { useBiLogger } from './useBiLogger';
import { getClickType, MouseButton } from '../helpers';
import { BiContext } from '../BiLogger.context';
var logger = ddt.createLogger('bilogger');
/** Returns true if the click event is trusted and used the middle or primary/left button */
export function isLoggableClickEvent(event) {
    return (event.isTrusted &&
        [MouseButton.Main, MouseButton.Auxiliary].includes(getClickType(event)));
}
function getTrackedElementAndEvent(trackedElement) {
    var eventType = 'mousedown';
    switch (trackedElement === null || trackedElement === void 0 ? void 0 : trackedElement.tagName.toLowerCase()) {
        case 'input':
            eventType = 'click';
            break;
        case 'option':
            eventType = 'change';
            // change events for option are at the select parent
            if (trackedElement.parentElement) {
                trackedElement = trackedElement.parentElement;
            }
            break;
        default:
    }
    return { eventType: eventType, trackedElement: trackedElement };
}
export function useBiClickLogger(biData, stopPropagation) {
    var logEvent = useBiLogger();
    var trackingRef = useRef();
    var setTrackingRef = useCallback(function (node) {
        trackingRef.current = node || undefined;
    }, []);
    var biXray = false; // useFlag('bi_xray');
    var biContext = useContext(BiContext);
    useEffect(function () {
        if (!biData) {
            return;
        }
        var _a = getTrackedElementAndEvent(trackingRef.current), eventType = _a.eventType, trackedElement = _a.trackedElement;
        if (!trackedElement) {
            if (process.env.NODE_ENV !== 'production') {
                logger.error('BiLogger childNode ref has not been set up');
            }
            return;
        }
        var eventHandler = function (e) {
            switch (eventType) {
                case 'mousedown':
                    // only allow left/mid clicks logging
                    if (!isLoggableClickEvent(e)) {
                        return true;
                    }
                    break;
                case 'change':
                    if (eventType === 'change' &&
                        biData._value !== trackedElement.value) {
                        return true;
                    }
                    break;
                default:
                    break;
            }
            logEvent(biData);
            if (stopPropagation) {
                e.stopPropagation();
            }
        };
        // leave capture mode off, so that descendant elements can stop propagation
        // and fire their own bi events if needed.
        trackedElement.addEventListener(eventType, eventHandler, false);
        return function () {
            return trackedElement &&
                trackedElement.removeEventListener(eventType, eventHandler, false);
        };
    }, [biData, logEvent, trackingRef, stopPropagation]);
    useEffect(function () {
        if (!biXray) {
            return;
        }
        var trackedElement = getTrackedElementAndEvent(trackingRef.current).trackedElement;
        if (!trackedElement) {
            return;
        }
        trackedElement.setAttribute('data-bidata', JSON.stringify(__assign(__assign({}, biContext), biData)));
        return function () { return trackedElement.removeAttribute('data-bidata'); };
    }, [biXray, biContext, biData]);
    return {
        setTrackingRef: setTrackingRef,
    };
}
//# sourceMappingURL=useBiClickLogger.js.map